const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const DEFAULT_CONNECTION_COLOR = 'rgba(160, 168, 172, 0.50)';
const CONNECTION_STROKE_WIDTH = '1.068';
const CYCLIC_CONNECTION_COLORS = [
	'rgba(13, 111, 196, 0.50)',
	'rgba(15, 157, 151, 0.50)',
	'rgba(109, 74, 224, 0.50)',
];

/**
 * Read stored diagram connections from the canvas element.
 *
 * @param {HTMLElement} diagramElement Diagram canvas.
 * @return {Array} Parsed connection list.
 */
const getConnections = ( diagramElement ) => {

	try {

		return JSON.parse( diagramElement?.dataset?.diagramConnections || '[]' );

	} catch ( error ) {

		return [];

	}

};

/**
 * Escape a value before using it inside an attribute selector.
 *
 * @param {string} value Attribute value.
 * @return {string} Escaped value.
 */
const escapeAttributeValue = ( value ) => {

	if ( window.CSS?.escape ) {

		return window.CSS.escape( value );

	}

	return value.replace( /"/g, '\\"' );

};

/**
 * Resolve the conceptual color index for a column, skipping narrow columns
 * so helper columns inherit the color of the previous full column.
 *
 * @param {HTMLElement|null} columnElement Diagram column element.
 * @param {HTMLElement[]} columnElements All diagram columns in DOM order.
 * @return {number|null} Effective conceptual column index.
 */
const getColumnColorIndex = ( columnElement, columnElements ) => {

	if ( ! columnElement ) {

		return null;

	}

	const targetColumnIndex = columnElements.indexOf( columnElement );
	if ( targetColumnIndex < 0 ) {

		return null;

	}

	let conceptualColumnIndex = -1;

	for ( let index = 0; index <= targetColumnIndex; index++ ) {

		if ( ! columnElements[ index ].classList.contains( 'is-width-narrow' ) ) {

			conceptualColumnIndex++;

		}

	}

	return conceptualColumnIndex >= 0 ? conceptualColumnIndex : 0;

};

/**
 * Check whether a diagram column is a narrow helper column.
 *
 * @param {HTMLElement|null} columnElement Diagram column element.
 * @return {boolean} True when the column is narrow.
 */
const isNarrowColumn = ( columnElement ) => columnElement?.classList.contains( 'is-width-narrow' ) || false;

/**
 * Resolve the full-width conceptual column used to place the color split.
 *
 * Narrow helper columns inherit the nearest full column on the relevant side.
 *
 * @param {HTMLElement|null} columnElement Diagram column element.
 * @param {HTMLElement[]} columnElements All diagram columns in DOM order.
 * @param {string} direction Search direction: previous or next.
 * @return {HTMLElement|null} Conceptual full-width column.
 */
const getConceptualColumnElement = ( columnElement, columnElements, direction ) => {

	if ( ! columnElement ) {

		return null;

	}

	if ( ! isNarrowColumn( columnElement ) ) {

		return columnElement;

	}

	const targetColumnIndex = columnElements.indexOf( columnElement );
	if ( targetColumnIndex < 0 ) {

		return columnElement;

	}

	const step = direction === 'previous' ? -1 : 1;

	for ( let index = targetColumnIndex + step; index >= 0 && index < columnElements.length; index += step ) {

		if ( ! isNarrowColumn( columnElements[ index ] ) ) {

			return columnElements[ index ];

		}

	}

	return columnElement;

};

/**
 * Resolve the cyclic accent color for a diagram column.
 *
 * @param {number|null} columnColorIndex Effective conceptual column index.
 * @return {string} Connection color.
 */
const resolveColumnConnectionColor = ( columnColorIndex ) => {

	if ( Number.isInteger( columnColorIndex ) ) {

		return CYCLIC_CONNECTION_COLORS[ columnColorIndex % CYCLIC_CONNECTION_COLORS.length ];

	}

	return DEFAULT_CONNECTION_COLOR;

};

/**
 * Read a numeric column index from a diagram item or column element.
 *
 * @param {HTMLElement|null} element Source element.
 * @return {number|null} Column index or null when unavailable.
 */
const getColumnIndex = ( element ) => {

	const columnIndex = Number.parseInt( element?.dataset?.diagramColumnIndex || '', 10 );

	return Number.isNaN( columnIndex ) ? null : columnIndex;

};

/**
 * Resolve the delay assigned to incoming lines for a target column.
 *
 * @param {HTMLElement|null} columnElement Target column element.
 * @return {number} Delay in milliseconds.
 */
const getIncomingLineDelay = ( columnElement ) => {

	const delay = Number.parseFloat( columnElement?.dataset?.diagramIncomingLineDelay || '0' );

	return Number.isNaN( delay ) ? 0 : delay;

};

/**
 * Interpolate between two points.
 *
 * @param {object} startPoint First point.
 * @param {object} endPoint Second point.
 * @param {number} ratio Interpolation ratio.
 * @return {object} Interpolated coordinates.
 */
const getInterpolatedPoint = ( startPoint, endPoint, ratio ) => ( {
	x: startPoint.x + ( ( endPoint.x - startPoint.x ) * ratio ),
	y: startPoint.y + ( ( endPoint.y - startPoint.y ) * ratio ),
} );

/**
 * Build the cubic bezier control points between two diagram items.
 *
 * @param {object} startPoint Source point.
 * @param {object} endPoint Target point.
 * @return {object} Cubic bezier definition.
 */
const createCurveDefinition = ( startPoint, endPoint ) => {

	const curveOffset = Math.max( 18, Math.abs( endPoint.x - startPoint.x ) * 0.14 );

	return {
		startPoint,
		controlPointOne: {
			x: startPoint.x + curveOffset,
			y: startPoint.y,
		},
		controlPointTwo: {
			x: endPoint.x - curveOffset,
			y: endPoint.y,
		},
		endPoint,
	};

};

/**
 * Resolve the point on a cubic bezier for a given interpolation ratio.
 *
 * @param {object} curveDefinition Cubic bezier definition.
 * @param {number} ratio Interpolation ratio.
 * @return {object} Point on the curve.
 */
const getCurvePoint = ( curveDefinition, ratio ) => {

	const { startPoint, controlPointOne, controlPointTwo, endPoint } = curveDefinition;
	const levelOnePointA = getInterpolatedPoint( startPoint, controlPointOne, ratio );
	const levelOnePointB = getInterpolatedPoint( controlPointOne, controlPointTwo, ratio );
	const levelOnePointC = getInterpolatedPoint( controlPointTwo, endPoint, ratio );
	const levelTwoPointA = getInterpolatedPoint( levelOnePointA, levelOnePointB, ratio );
	const levelTwoPointB = getInterpolatedPoint( levelOnePointB, levelOnePointC, ratio );

	return {
		levelOnePointA,
		levelOnePointB,
		levelOnePointC,
		levelTwoPointA,
		levelTwoPointB,
		splitPoint: getInterpolatedPoint( levelTwoPointA, levelTwoPointB, ratio ),
	};

};

/**
 * Find the interpolation ratio that places the split near a target x coordinate.
 *
 * @param {object} curveDefinition Cubic bezier definition.
 * @param {number} targetX Desired split x coordinate.
 * @return {number} Interpolation ratio.
 */
const getSplitRatioForX = ( curveDefinition, targetX ) => {

	let minRatio = 0.05;
	let maxRatio = 0.95;

	for ( let iteration = 0; iteration < 12; iteration++ ) {

		const currentRatio = ( minRatio + maxRatio ) / 2;
		const { splitPoint } = getCurvePoint( curveDefinition, currentRatio );

		if ( splitPoint.x < targetX ) {

			minRatio = currentRatio;

		} else {

			maxRatio = currentRatio;

		}

	}

	return ( minRatio + maxRatio ) / 2;

};

/**
 * Create two connected bezier segments so each half can use the color
 * of the column it visually belongs to.
 *
 * @param {object} startPoint Source point.
 * @param {object} endPoint Target point.
 * @param {number} splitX X coordinate where the color split should happen.
 * @return {string[]} SVG path definitions for the source and target halves.
 */
const createSplitPathDefinitions = ( startPoint, endPoint, splitX ) => {

	const curveDefinition = createCurveDefinition( startPoint, endPoint );
	const splitRatio = getSplitRatioForX(
		curveDefinition,
		Math.min( Math.max( splitX, startPoint.x + 1 ), endPoint.x - 1 )
	);
	const {
		levelOnePointA,
		levelOnePointC,
		levelTwoPointA,
		levelTwoPointB,
		splitPoint,
	} = getCurvePoint( curveDefinition, splitRatio );

	return [
		[
			`M ${ startPoint.x } ${ startPoint.y }`,
			`C ${ levelOnePointA.x } ${ levelOnePointA.y }`,
			`${ levelTwoPointA.x } ${ levelTwoPointA.y }`,
			`${ splitPoint.x } ${ splitPoint.y }`,
		].join( ' ' ),
		[
			`M ${ splitPoint.x } ${ splitPoint.y }`,
			`C ${ levelTwoPointB.x } ${ levelTwoPointB.y }`,
			`${ levelOnePointC.x } ${ levelOnePointC.y }`,
			`${ endPoint.x } ${ endPoint.y }`,
		].join( ' ' ),
	];

};

/**
 * Create a single bezier path definition between two points.
 *
 * @param {object} startPoint Source point.
 * @param {object} endPoint Target point.
 * @return {string} SVG path definition.
 */
const createPathDefinition = ( startPoint, endPoint ) => {

	const { controlPointOne, controlPointTwo } = createCurveDefinition( startPoint, endPoint );

	return [
		`M ${ startPoint.x } ${ startPoint.y }`,
		`C ${ controlPointOne.x } ${ controlPointOne.y }`,
		`${ controlPointTwo.x } ${ controlPointTwo.y }`,
		`${ endPoint.x } ${ endPoint.y }`,
	].join( ' ' );

};

/**
 * Create a stroked path element.
 *
 * @param {Document} documentRef Document reference.
 * @param {string} pathDefinition SVG path definition.
 * @param {string} strokeValue Stroke color or gradient reference.
 * @return {SVGPathElement} SVG path node.
 */
const createPathElement = ( documentRef, pathDefinition, strokeValue ) => {

	const path = documentRef.createElementNS( SVG_NAMESPACE, 'path' );

	path.setAttribute( 'd', pathDefinition );
	path.setAttribute( 'fill', 'none' );
	path.setAttribute( 'stroke', strokeValue );
	path.setAttribute( 'stroke-width', CONNECTION_STROKE_WIDTH );
	path.setAttribute( 'stroke-linecap', 'round' );
	path.setAttribute( 'stroke-opacity', '1' );
	path.setAttribute( 'stroke-linejoin', 'round' );
	path.style.setProperty( '--diagram-path-length', `${ path.getTotalLength() }` );

	return path;

};

/**
 * Draw all connection paths for a single diagram instance.
 *
 * @param {HTMLElement} diagramElement Diagram canvas.
 * @return {void}
 */
export const drawDiagramLines = ( diagramElement ) => {

	if ( ! diagramElement ) {

		return;

	}

	const contentElement = diagramElement.querySelector( '.b-diagram__content' );
	const svgElement = diagramElement.querySelector( '.b-diagram__lines' );

	if ( ! contentElement || ! svgElement ) {

		return;

	}

	const diagramRect = diagramElement.getBoundingClientRect();
	const contentRect = contentElement.getBoundingClientRect();
	const connections = getConnections( diagramElement );
	const diagramBlockElement = diagramElement.closest( '.b-diagram' );
	const columnElements = Array.from( contentElement.querySelectorAll( '.b-diagram__column' ) );
	const lineStaggerDelay = Number.parseFloat( diagramBlockElement?.dataset?.diagramLineStaggerDelay || '120' );
	const incomingLineOrders = new Map();

	svgElement.replaceChildren();
	svgElement.style.left = `${ contentRect.left - diagramRect.left }px`;
	svgElement.style.top = `${ contentRect.top - diagramRect.top }px`;
	svgElement.style.width = `${ contentRect.width }px`;
	svgElement.style.height = `${ contentRect.height }px`;
	svgElement.setAttribute( 'viewBox', `0 0 ${ Math.max( contentRect.width, 1 ) } ${ Math.max( contentRect.height, 1 ) }` );

	connections.forEach( ( connection ) => {

		const startElement = contentElement.querySelector(
			`[data-item-id="${ escapeAttributeValue( connection.from ) }"]`
		);
		const endElement = contentElement.querySelector(
			`[data-item-id="${ escapeAttributeValue( connection.to ) }"]`
		);

		if ( ! startElement || ! endElement ) {

			return;

		}

		const startRect = startElement.getBoundingClientRect();
		const endRect = endElement.getBoundingClientRect();
		const startColumnElement = startElement.closest( '.b-diagram__column' );
		const endColumnElement = endElement.closest( '.b-diagram__column' );
		const sourceColumnIndex = getColumnIndex( startColumnElement || startElement );
		const targetColumnIndex = getColumnIndex( endColumnElement || endElement );
		const sourceColumnColorIndex = getColumnColorIndex( startColumnElement, columnElements );
		const targetColumnColorIndex = getColumnColorIndex( endColumnElement, columnElements );
		const sourceConceptualColumnElement = getConceptualColumnElement( startColumnElement, columnElements, 'previous' );
		const targetConceptualColumnElement = getConceptualColumnElement( endColumnElement, columnElements, 'next' );
		const lineGroupKey = `${ targetColumnIndex ?? 'none' }`;
		const lineOrder = incomingLineOrders.get( lineGroupKey ) || 0;
		const startPoint = {
			x: startRect.right - contentRect.left,
			y: startRect.top - contentRect.top + ( startRect.height / 2 ),
		};
		const endPoint = {
			x: endRect.left - contentRect.left,
			y: endRect.top - contentRect.top + ( endRect.height / 2 ),
		};
		const lineDelay = `${ getIncomingLineDelay( endColumnElement ) + ( lineOrder * ( Number.isNaN( lineStaggerDelay ) ? 120 : lineStaggerDelay ) ) }ms`;
		const shouldUseSingleColor =
			isNarrowColumn( startColumnElement ) ||
			isNarrowColumn( endColumnElement );
		const pathElements = shouldUseSingleColor ?
			[
				createPathElement(
					document,
					createPathDefinition( startPoint, endPoint ),
					resolveColumnConnectionColor(
						isNarrowColumn( startColumnElement ) ?
							targetColumnColorIndex :
							sourceColumnColorIndex
					)
				),
			] :
			[
				createPathElement(
					document,
					createSplitPathDefinitions(
						startPoint,
						endPoint,
						(
							( ( sourceConceptualColumnElement?.getBoundingClientRect().right || startRect.right ) - contentRect.left ) +
							( ( targetConceptualColumnElement?.getBoundingClientRect().left || endRect.left ) - contentRect.left )
						) / 2
					)[ 0 ],
					resolveColumnConnectionColor( sourceColumnColorIndex )
				),
				createPathElement(
					document,
					createSplitPathDefinitions(
						startPoint,
						endPoint,
						(
							( ( sourceConceptualColumnElement?.getBoundingClientRect().right || startRect.right ) - contentRect.left ) +
							( ( targetConceptualColumnElement?.getBoundingClientRect().left || endRect.left ) - contentRect.left )
						) / 2
					)[ 1 ],
					resolveColumnConnectionColor( targetColumnColorIndex )
				),
			];

		pathElements.forEach( ( pathElement ) => {

			pathElement.style.setProperty( '--diagram-line-order', svgElement.childElementCount );
			pathElement.style.setProperty( '--diagram-line-delay', lineDelay );
			pathElement.dataset.diagramSourceColumn = `${ sourceColumnIndex ?? '' }`;
			pathElement.dataset.diagramTargetColumn = `${ targetColumnIndex ?? '' }`;
			svgElement.appendChild( pathElement );

		} );

		incomingLineOrders.set( lineGroupKey, lineOrder + 1 );

	} );

};

/**
 * Start observers that redraw connections when the layout changes.
 *
 * @param {HTMLElement} diagramElement Diagram canvas.
 * @return {Function|undefined} Cleanup callback.
 */
export const initializeDiagramLines = ( diagramElement ) => {

	if ( ! diagramElement ) {

		return undefined;

	}

	const contentElement = diagramElement.querySelector( '.b-diagram__content' );

	if ( ! contentElement ) {

		return undefined;

	}

	let frameId = 0;

	/**
	 * Schedule a single redraw on the next animation frame.
	 *
	 * @return {void}
	 */
	const requestDraw = () => {

		window.cancelAnimationFrame( frameId );
		frameId = window.requestAnimationFrame( () => drawDiagramLines( diagramElement ) );

	};
	const contentMutationObserver = new MutationObserver( requestDraw );
	const connectionMutationObserver = new MutationObserver( requestDraw );
	const resizeObserver = typeof ResizeObserver === 'undefined' ?
		null :
		new ResizeObserver( requestDraw );

	requestDraw();
	contentMutationObserver.observe( contentElement, {
		childList: true,
		characterData: true,
		subtree: true,
	} );
	connectionMutationObserver.observe( diagramElement, {
		attributes: true,
		attributeFilter: [ 'data-diagram-connections' ],
	} );
	resizeObserver?.observe( contentElement );
	window.addEventListener( 'resize', requestDraw );

	return () => {

		window.cancelAnimationFrame( frameId );
		contentMutationObserver.disconnect();
		connectionMutationObserver.disconnect();
		resizeObserver?.disconnect();
		window.removeEventListener( 'resize', requestDraw );

	};

};

/**
 * Initialize every diagram instance found on the page.
 *
 * @return {void}
 */
export const initializeAllDiagramLines = () => {

	document.querySelectorAll( '.b-diagram__canvas' ).forEach( ( diagramElement ) => {

		initializeDiagramLines( diagramElement );

	} );

};
